import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  CalendarDays,
  ClipboardList,
  Clock,
  Grid2X2,
  LayoutDashboard,
  Map,
  Network,
  Package,
  Phone,
  Settings,
  ShoppingCart,
  TicketPercent,
  Users,
  UtensilsCrossed,
  Globe,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { useAppStore } from '@/lib/store/useAppStore'
import { useNavCounts } from '@/hooks/useNavCounts'
import { cn } from '@/lib/utils'

type MobileNavItem = {
  to: string
  label: string
  icon: React.ElementType
  count?: number
}

function MobileNavLink({ item, onNavigate }: { item: MobileNavItem; onNavigate?: () => void }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) => cn(
        'relative flex min-h-12 min-w-0 items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]',
      )}
    >
      <Icon className="h-5 w-5 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {!!item.count && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-accent-amber)] px-1.5 text-[10px] font-bold leading-none text-black">
          {item.count > 9 ? '9+' : item.count}
        </span>
      )}
    </NavLink>
  )
}

export function MobileNavigation() {
  const { t } = useTranslation()
  const location = useLocation()
  const [moreOpen, setMoreOpen] = useState(false)
  const { featurePOS, featureSheAnswers, featureCustomerPage } = useAppStore()
  const counts = useNavCounts()

  const primaryItems: MobileNavItem[] = [
    { to: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard },
    { to: '/bookings', label: t('nav.bookings'), icon: CalendarDays, count: counts.bookings },
    { to: '/floor-plan', label: t('nav.floorPlan'), icon: Map },
    { to: '/pos', label: featurePOS ? t('nav.pos') : t('nav.menu'), icon: featurePOS ? ShoppingCart : UtensilsCrossed, count: featurePOS ? counts.pos : undefined },
  ]
  const isPrimaryRoute = primaryItems.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`))

  const moreSections: Array<{ label: string; items: MobileNavItem[] }> = [
    {
      label: 'Service',
      items: [
        { to: '/orders', label: t('nav.orders'), icon: ClipboardList, count: counts.collections },
        { to: '/customers', label: t('nav.customers'), icon: Users },
        ...(featurePOS ? [{ to: '/collections', label: t('nav.collections'), icon: Package, count: counts.collections }] : []),
        ...(featureCustomerPage ? [{ to: '/coupons', label: 'Coupons', icon: TicketPercent }] : []),
      ],
    },
    {
      label: 'Online',
      items: [
        ...(featureSheAnswers ? [{ to: '/she-answers', label: t('nav.sheAnswers'), icon: Phone }] : []),
        ...(featureCustomerPage ? [{ to: '/customer-page', label: t('nav.customerPage'), icon: Globe }] : []),
        { to: '/ordevo-network', label: t('nav.ordevoNetwork'), icon: Network },
      ],
    },
    {
      label: 'Setup',
      items: [
        { to: '/opening-hours', label: t('nav.openingHours'), icon: Clock },
        { to: '/settings', label: t('nav.settings'), icon: Settings },
      ],
    },
  ].filter((section) => section.items.length > 0)

  return (
    <>
      <nav
        aria-label="Main navigation"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)]/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_24px_rgba(0,0,0,0.12)] backdrop-blur-xl md:hidden"
      >
        <div className="mx-auto grid h-16 max-w-xl grid-cols-5">
          {primaryItems.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.to}
                to={item.to}
                aria-label={item.label}
                className={({ isActive }) => cn(
                  'relative flex min-w-0 flex-col items-center justify-center gap-1 px-1 text-[10px] font-semibold transition-colors',
                  isActive ? 'text-[var(--color-accent-amber)]' : 'text-[var(--color-text-muted)]',
                )}
              >
                {({ isActive }) => (
                  <>
                    <span className={cn('relative flex h-7 w-12 items-center justify-center rounded-full transition-colors', isActive && 'bg-[var(--color-accent-amber-dim)]')}>
                      <Icon className="h-5 w-5" />
                      {!!item.count && (
                        <span className="absolute -right-0.5 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-accent-amber)] px-1 text-[9px] font-bold leading-none text-black">
                          {item.count > 9 ? '9+' : item.count}
                        </span>
                      )}
                    </span>
                    <span className="max-w-full truncate">{item.label}</span>
                  </>
                )}
              </NavLink>
            )
          })}
          <button
            type="button"
            aria-label="More navigation options"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(true)}
            className={cn(
              'flex min-w-0 flex-col items-center justify-center gap-1 px-1 text-[10px] font-semibold transition-colors',
              moreOpen || !isPrimaryRoute ? 'text-[var(--color-accent-amber)]' : 'text-[var(--color-text-muted)]',
            )}
          >
            <span className={cn('flex h-7 w-12 items-center justify-center rounded-full transition-colors', (moreOpen || !isPrimaryRoute) && 'bg-[var(--color-accent-amber-dim)]')}>
              <Grid2X2 className="h-5 w-5" />
            </span>
            <span>More</span>
          </button>
        </div>
      </nav>

      <Modal open={moreOpen} onClose={() => setMoreOpen(false)} title="More" size="md">
        <div className="-mx-1 -my-2 space-y-5">
          {moreSections.map((section) => (
            <section key={section.label}>
              <h3 className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
                {section.label}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <MobileNavLink key={item.to} item={item} onNavigate={() => setMoreOpen(false)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </Modal>
    </>
  )
}
